import { observer } from 'mobx-react-lite'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Radom, { WithdrawalAccount } from '../state/Radom'
import Treasury from '../state/Treasury'
import { getMethod } from '../util/Managed'
import { errorToast, shortAddress, successToast } from '../util/Util'
import { PrimaryButton, SecondaryButton } from './Button'
import Dropdown, { DropdownItem } from './Dropdown'
import { InputLabel } from './Input'
import LoadingBar from './LoadingBar'
import Modal, { ModalFooter } from './Modal'
import NumberInput from './NumberInput'

const WithdrawalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  cursor: default;
`

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  border-radius: 13px;
  border: 1px solid #eceff3;
  background: #fbfcfe;
`

const AssetSummaryCard = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid #e7ebf2;
  border-radius: 13px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 11px 13px;
`

export interface WithdrawableAsset {
  network: string
  token?: string
  maxAmount: string
}

interface IProps {
  modalOpen: boolean
  onModalOpen: (open: boolean) => void
  assetToWithdraw: WithdrawableAsset
}

const AssetWithdrawalModal = observer((props: IProps): ReactElement => {
  const navigate = useNavigate()

  const [amountToWithdraw, setWithdrawalAmount] = useState('')
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount>()
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const senderPaysFees = false

  const [logoUrl, setLogoUrl] = useState('')
  const [assetName, setAssetName] = useState('')

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])

  const filterWithdrawalAccount = (a: WithdrawalAccount): boolean => {
    if (a.accountDetails.Crypto && a.accountDetails.Crypto.network === props.assetToWithdraw.network) {
      return true
    }

    if (a.accountDetails.Fiat) {
      return true
    }

    return false
  }

  const filteredAccounts = withdrawalAccounts
    .filter(a => filterWithdrawalAccount(a))

  const renderAccountText = (account: WithdrawalAccount): React.ReactElement => {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      {
        account.accountDetails.Crypto &&
        <img
          src={getMethod(account.accountDetails.Crypto.network).logo}
          style={{
            height: 20,
            width: 'auto',
            maxWidth: 20,
            flexShrink: 0
          }} />
      }
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: '#20242d',
          fontSize: 13.5,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {account.name}
        </span>
        {
          account.accountDetails.Crypto &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shortAddress(account.accountDetails.Crypto.address)}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.iban &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            SEPA · {account.accountDetails.Fiat.accountType.iban.iban}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.us &&
          <span style={{ color: '#7d8492', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ACH · {account.accountDetails.Fiat.accountType.us.accountNumber}
          </span>
        }
      </div>
    </div>
  }

  useEffect(() => {
    setIsLoadingAccounts(true)
    Radom
      .listWithdrawalAccounts()
      .then(a => setWithdrawalAccounts(a))
      .finally(() => setIsLoadingAccounts(false))
  }, [])

  useEffect(() => {
    const { network, token } = props.assetToWithdraw
    setLogoUrl(getMethod(network, token).logo)
    setAssetName(getMethod(network, token).name)

    setWithdrawalAccount(undefined)
    setWithdrawalAmount('')
  }, [props.assetToWithdraw])

  const onTokenWithdrawal = async (): Promise<void> => {
    if (!withdrawalAccount) {
      return
    }

    let isError = false

    setIsWithdrawing(true)
    const { network, token } = props.assetToWithdraw
    try {
      await Radom.submitWithdrawalRequest(network, amountToWithdraw, withdrawalAccount.id, senderPaysFees, token)
      successToast('Successfully submitted withdrawal request')
      Treasury.loadBalances()
    } catch (err) {
      isError = true
      console.error(err)
      errorToast(err.response?.data?.error || err.message)
    }
    setIsWithdrawing(false)

    if (!isError) {
      props.onModalOpen(false)
    }
  }

  return (
    <Modal visible={props.modalOpen}
      innerStyles={{ width: 476 }}
      onClose={() => props.onModalOpen(false)}
      title='Withdraw'>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <div style={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'start',
          gap: 12,
          padding: 0
        }}>
          <SectionCard>
            <AssetSummaryCard>
              <WithdrawalRow>
                <div style={{
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundImage: `url(${logoUrl})`,
                  minWidth: 34,
                  height: 34,
                  flexShrink: 0
                }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <span style={{ fontSize: 11.25, opacity: 0.62, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{assetName}</span>
                  <span style={{ color: '#20242d', fontSize: 15.25, fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    Ready to withdraw
                  </span>
                </div>
              </WithdrawalRow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right' }}>
                <span style={{ color: '#6b7280', fontSize: 11.5 }}>Available balance</span>
                <span style={{ color: '#20242d', fontSize: 15.25, fontWeight: 500 }}>
                  {props.assetToWithdraw.maxAmount}
                </span>
              </div>
            </AssetSummaryCard>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <InputLabel style={{ opacity: 1 }}>Withdrawal amount</InputLabel>
              <div style={{ display: 'flex', flexGrow: 1, gap: 10 }}>
                <NumberInput
                  disabled={isWithdrawing}
                  value={amountToWithdraw}
                  onUserInput={e => setWithdrawalAmount(e)}
                  fontSize='14.5'
                  align='left'
                  style={{ minHeight: 40 }}
                  placeholder='Enter amount'
                  required={true}
                />
                <SecondaryButton
                  style={{ minHeight: 40, minWidth: 66, padding: '0 12px', flexShrink: 0 }}
                  disabled={isWithdrawing}
                  onClick={() => setWithdrawalAmount(props.assetToWithdraw.maxAmount)}>
                  Max
                </SecondaryButton>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <InputLabel style={{ opacity: 1 }}>Withdrawal destination</InputLabel>
              {
                isLoadingAccounts
                  ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 40 }} />
                  : filteredAccounts.length > 0
                    ? <Dropdown
                      disabled={isLoadingAccounts}
                      outerStyle={{ minHeight: 40 }}
                      selectedContent={
                        withdrawalAccount
                          ? renderAccountText(withdrawalAccount)
                          : <span style={{ fontSize: 13.5, color: '#747894' }}>Select account</span>
                      }
                      dropdownContent={
                        filteredAccounts
                          .filter(t => t.id !== withdrawalAccount?.id)
                          .map(t =>
                            <DropdownItem
                              key={t.id}
                              onClick={() => {
                                setWithdrawalAccount(t)
                              }}
                              style={{ padding: '11px 12px' }}>
                              {renderAccountText(t)}
                            </DropdownItem>
                          )
                      } />
                    : <SecondaryButton onClick={() => {
                      navigate('/accounts')
                      props.onModalOpen(false)
                    }} style={{ width: '100%', minHeight: 40 }}>Create withdrawal account</SecondaryButton>

              }
            </div>

          </SectionCard>
        </div>
        {
          filteredAccounts.length > 0 &&
          <ModalFooter $fullWidthAction>
            <PrimaryButton
              disabled={!amountToWithdraw || !withdrawalAccount}
              isLoading={isWithdrawing}
              style={{ width: '100%' }}
              onClick={onTokenWithdrawal}>
              Withdraw
            </PrimaryButton>
          </ModalFooter>
        }
      </div>
    </Modal>
  )
})

export default AssetWithdrawalModal
